import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import DemoCTA from '../../ui/DemoCTA';
import './Education.css';

const Education = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="education-page">
      <Helmet>
        <title>AI Chat Assist for Education | 24/7 Support & Admissions Help</title>
        <meta
          name="description"
          content="AI chatbot solutions for educational institutions to automate admissions, offer 24/7 student support, and enhance campus engagement and operational efficiency."
        />
        <meta
          name="keywords"
          content="AI Solutions For Education, AI powered Solution, AI solutions, AI assisted chatbot solutions for educational institutions, ai based customer support, AI Insights, marketing campaigns with AI"
        />
        <link rel="canonical" href="https://www.aichatassist.com/industries/education" />

        {/* Open Graph Meta */}
        <meta
          property="og:title"
          content="AI Chat Assist for Education | 24/7 Student Help & Admissions"
        />
        <meta
          property="og:description"
          content="AI assisted chatbots for schools, colleges & universities to automate admissions, provide 24/7 student support, and boost campus engagement and efficiency."
        />
        <meta property="og:url" content="https://www.aichatassist.com/industries/education" />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://admin.aichatassist.com/uploads/Wordpress_Plugin_Banner_6964048f39.jpg"
        />
        <meta property="og:site_name" content="AI Chat Assist" />
        <meta property="og:locale" content="en_US" />

        {/* Twitter Card Meta */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="AI Chat Assist for Education | 24/7 Student Help & Admissions"
        />
        <meta
          name="twitter:description"
          content="AI chatbot solutions for educational institutions to automate admissions, provide 24/7 student support, and boost campus engagement."
        />
        <meta
          name="twitter:image"
          content="https://admin.aichatassist.com/uploads/Wordpress_Plugin_Banner_6964048f39.jpg"
        />
        <meta name="twitter:site" content="@aichatassist" />
      </Helmet>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="container h-100 d-flex align-items-center">
          <div className="row justify-content-center w-100">
            <div className=" text-center hero-content">
              <h1 className="hero-title mb-4">
                <span className="hero-highlight">AI Solutions </span> For Education
              </h1>
              <div className="underline-accent mx-auto"></div>
              <p className="hero-description mt-4">
                Streamline campus operations, enhance student engagement, and offer round-the-clock
                support using AI assisted chatbot solutions for educational institutions and online
                learning platforms.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Spacer */}
      <div className="py-5"></div>

      {/* Key Challenges Section */}
      <section className="challenges-section section-padding">
        <div className="container">
          <div className="row mb-5">
            <div className="col-lg-8 mx-auto text-center">
              <h2 className="section-title">
                <span className="hero-highlight">Key Challenges</span> in Education
              </h2>
              <p className="section-description">
                Educational institutions face rising expectations in admissions and student support.
                Our AI powered solution helps overcome these challenges with automated assistance
                and personalized engagement.
              </p>
            </div>
          </div>

          <div className="row g-4 justify-content-center">
            <div className="col-md-6 col-lg-4">
              <div className="edu-challenge-card h-100 shadow-sm">
                <div className="challenge-icon-wrapper">
                  <div className="challenge-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                    </svg>
                  </div>
                </div>
                <h5 className="challenge-title">24/7 Student Inquiries</h5>
                <p className="challenge-description">
                  Students expect real-time answers anytime. AI assisted chatbot solutions for
                  educational institutions ensure instant, round-the-clock responses.
                </p>
              </div>
            </div>

            <div className="col-md-6 col-lg-4">
              <div className="edu-challenge-card h-100 shadow-sm">
                <div className="challenge-icon-wrapper">
                  <div className="challenge-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                      <circle cx="9" cy="7" r="4"></circle>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                    </svg>
                  </div>
                </div>
                <h5 className="challenge-title">Admissions Automation</h5>
                <p className="challenge-description">
                  Simplify application workflows with AI powered solution for education — guiding
                  students through programs, deadlines, and document requirements effortlessly.
                </p>
              </div>
            </div>

            <div className="col-md-6 col-lg-4">
              <div className="edu-challenge-card h-100 shadow-sm">
                <div className="challenge-icon-wrapper">
                  <div className="challenge-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"></path>
                    </svg>
                  </div>
                </div>
                <h5 className="challenge-title">Staff & Budget Challenges</h5>
                <p className="challenge-description">
                  Reduce support load using AI assisted chatbot solutions for educational
                  institutions, providing consistent help without overextending resources.
                </p>
              </div>
            </div>

            <div className="col-md-6 col-lg-4">
              <div className="edu-challenge-card h-100 shadow-sm">
                <div className="challenge-icon-wrapper">
                  <div className="challenge-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                    </svg>
                  </div>
                </div>
                <h5 className="challenge-title">Campus Engagement</h5>
                <p className="challenge-description">
                  Enhance learning and participation using AI powered solution to boost
                  interactivity across online and offline education formats.
                </p>
              </div>
            </div>

            <div className="col-md-6 col-lg-4">
              <div className="edu-challenge-card h-100 shadow-sm">
                <div className="challenge-icon-wrapper">
                  <div className="challenge-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                      <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                      <line x1="12" y1="22.08" x2="12" y2="12"></line>
                    </svg>
                  </div>
                </div>
                <h5 className="challenge-title">Information Clarity</h5>
                <p className="challenge-description">
                  AI assisted chatbot solutions for educational institutions simplify access to
                  important information and reduce confusion.
                </p>
              </div>
            </div>

            <div className="col-md-6 col-lg-4">
              <div className="edu-challenge-card h-100 shadow-sm">
                <div className="challenge-icon-wrapper">
                  <div className="challenge-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="3" y1="9" x2="21" y2="9"></line>
                      <line x1="9" y1="21" x2="9" y2="9"></line>
                    </svg>
                  </div>
                </div>
                <h5 className="challenge-title">Global & 24/7 Student Support</h5>
                <p className="challenge-description">
                  Offer round-the-clock ai based customer support in multiple languages to assist
                  international students anytime, anywhere.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section id="use-cases" className="use-cases-section section-padding">
        <div className="container">
          <div className="row mb-5">
            <div
              className="col-lg-8 mx-auto text-center use-cases-text"
              data-component-name="UseCasesText"
            >
              <h2 className="section-title">
                <span className="hero-highlight">Use Cases</span>
              </h2>
              <p className="section-description">
                How educational institutions leverage our platform to solve key challenges
              </p>
            </div>
          </div>

          <div className="row g-4" data-component-name="Education">
            <div className="col-md-6">
              <div className="industry-card">
                <img
                  src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1740&q=80"
                  alt="Student Enrollment & Admissions"
                  className="industry-card-img"
                  data-component-name="Education"
                />
                <div className="industry-card-overlay">
                  <h4 className="industry-card-title">Student Enrollment & Admissions</h4>
                </div>
                <div className="industry-card-body" data-component-name="Education">
                  <h4 className="industry-card-title">Student Enrollment & Admissions</h4>
                  <p className="industry-hover-text"></p>
                  <ul className="feature-list">
                    <li>24/7 application assistance</li>
                    <li>Program information delivery</li>
                    <li>Document submission guidance</li>
                    <li>Deadline reminders</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="industry-card">
                <img
                  src="https://images.unsplash.com/photo-1434030216411-0b793f4b4173?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1740&q=80"
                  alt="Student Support Services"
                  className="industry-card-img"
                  data-component-name="Education"
                />
                <div className="industry-card-overlay" data-component-name="Education">
                  <h4 className="industry-card-title">Student Support Services</h4>
                </div>
                <div className="industry-card-body" data-component-name="Education">
                  <h4 className="industry-card-title" data-component-name="Education">
                    Student Support Services
                  </h4>
                  <p className="industry-hover-text"></p>
                  <ul className="feature-list">
                    <li>Financial aid information</li>
                    <li>Registration guidance</li>
                    <li>Campus resource navigation</li>
                    <li>Academic policy clarification</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="industry-card">
                <img
                  src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1742&q=80"
                  alt="Course Selection & Registration"
                  className="industry-card-img"
                />
                <div className="industry-card-overlay">
                  <h4 className="industry-card-title">Course Selection & Registration</h4>
                </div>
                <div className="industry-card-body">
                  <h4 className="industry-card-title">Course Selection & Registration</h4>
                  <p className="industry-hover-text"></p>
                  <ul className="feature-list">
                    <li>Course recommendation</li>
                    <li>Prerequisite verification</li>
                    <li>Schedule planning assistance</li>
                    <li>Registration troubleshooting</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="industry-card">
                <img
                  src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1740&q=80"
                  alt="Faculty & Staff Support"
                  className="industry-card-img"
                />
                <div className="industry-card-overlay">
                  <h4 className="industry-card-title">Faculty & Staff Support</h4>
                </div>
                <div className="industry-card-body">
                  <h4 className="industry-card-title">Faculty & Staff Support</h4>
                  <p className="industry-hover-text"></p>
                  <ul className="feature-list">
                    <li>IT support triage</li>
                    <li>Policy and procedure guidance</li>
                    <li>Resource request processing</li>
                    <li>Administrative task automation</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Institution Types Section */}
      <section className="institution-types-section section-padding">
        <div className="container">
          <div className="row mb-5">
            <div className="col-lg-8 mx-auto text-center">
              <h2 className="section-title">
                <span className="hero-highlight">Tailored Solutions for</span> All Institutions
              </h2>
              <p className="section-description">
                Specialized capabilities for different educational environments
              </p>
            </div>
          </div>

          <div className="row g-4 justify-content-center">
            <div className="col-md-4">
              <div className="solution-card h-100">
                <div className="solution-header text-center">
                  <h3 className="fw-semibold">Higher Education</h3>
                  <p className="text-muted">Universities and colleges</p>
                </div>
                <div className="solution-content">
                  <ul className="solution-features list-unstyled ps-3">
                    <li className="d-flex align-items-center mb-3">
                      <div className="feature-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="feature-check-icon"
                        >
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </div>
                      <span>AI-powered admissions process</span>
                    </li>
                    <li className="d-flex align-items-center mb-3">
                      <div className="feature-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="feature-check-icon"
                        >
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </div>
                      <span>Campus navigation with maps</span>
                    </li>
                    <li className="d-flex align-items-center mb-3">
                      <div className="feature-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="feature-check-icon"
                        >
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </div>
                      <span>International student visa support</span>
                    </li>
                    <li className="d-flex align-items-center mb-3">
                      <div className="feature-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="feature-check-icon"
                        >
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </div>
                      <span>Library resource recommendations</span>
                    </li>
                    <li className="d-flex align-items-center mb-3">
                      <div className="feature-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="feature-check-icon"
                        >
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </div>
                      <span>Academic advising chatbots</span>
                    </li>
                    <li className="d-flex align-items-center mb-3">
                      <div className="feature-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="feature-check-icon"
                        >
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </div>
                      <span>Financial aid assistance</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="solution-card h-100">
                <div className="solution-header text-center">
                  <h3 className="fw-semibold">K-12 Schools</h3>
                  <p className="text-muted">Primary and secondary education</p>
                </div>
                <div className="solution-content">
                  <ul className="solution-features list-unstyled ps-3">
                    <li className="d-flex align-items-center mb-3">
                      <div className="feature-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="feature-check-icon"
                        >
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </div>
                      <span>Real-time parent-teacher communication</span>
                    </li>
                    <li className="d-flex align-items-center mb-3">
                      <div className="feature-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="feature-check-icon"
                        >
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </div>
                      <span>Personalized homework help</span>
                    </li>
                    <li className="d-flex align-items-center mb-3">
                      <div className="feature-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="feature-check-icon"
                        >
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </div>
                      <span>School calendar with reminders</span>
                    </li>
                    <li className="d-flex align-items-center mb-3">
                      <div className="feature-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="feature-check-icon"
                        >
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </div>
                      <span>Safe student social platform</span>
                    </li>
                    <li className="d-flex align-items-center mb-3">
                      <div className="feature-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="feature-check-icon"
                        >
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </div>
                      <span>Attendance tracking alerts</span>
                    </li>
                    <li className="d-flex align-items-center mb-3">
                      <div className="feature-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="feature-check-icon"
                        >
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </div>
                      <span>Student progress insights</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="solution-card h-100">
                <div className="solution-header text-center">
                  <h3 className="fw-semibold">Online Learning</h3>
                  <p className="text-muted">EdTech and e-learning platforms</p>
                </div>
                <div className="solution-content">
                  <ul className="solution-features list-unstyled ps-3">
                    <li className="d-flex align-items-center mb-3">
                      <div className="feature-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="feature-check-icon"
                        >
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </div>
                      <span>Interest-based course recommendations</span>
                    </li>
                    <li className="d-flex align-items-center mb-3">
                      <div className="feature-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="feature-check-icon"
                        >
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </div>
                      <span>Adaptive learning paths</span>
                    </li>
                    <li className="d-flex align-items-center mb-3">
                      <div className="feature-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="feature-check-icon"
                        >
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </div>
                      <span>24/7 technical support</span>
                    </li>
                    <li className="d-flex align-items-center mb-3">
                      <div className="feature-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="feature-check-icon"
                        >
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </div>
                      <span>Gamified achievement tracking</span>
                    </li>
                    <li className="d-flex align-items-center mb-3">
                      <div className="feature-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="feature-check-icon"
                        >
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </div>
                      <span>Interactive learning materials</span>
                    </li>
                    <li className="d-flex align-items-center mb-3">
                      <div className="feature-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="feature-check-icon"
                        >
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </div>
                      <span>Skill gap analysis</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Education;
